import { computed } from 'vue';
import { queryOptions, useQueryClient } from '@tanstack/vue-query';
import type {
  MakerOrganisationType,
  PartnerOrganisationListRow,
} from '@cutr/constants/organisations';
import {
  getPartnerAnnualGmv,
  getPartnerOrganisation,
  getPartnerOrganisationProfile,
  getPartnerOrganisations,
} from '@/api/partnerOrganisations';
import { partnerOrganisationKeys } from './keys';
import {
  PARTNER_ORGANISATION_DETAIL_STALE_TIME_MS,
  PARTNER_ORGANISATION_PROFILE_STALE_TIME_MS,
  PARTNER_ORGANISATIONS_STALE_TIME_MS,
} from './shared/staleTimes';
import { useCatalogueQuery } from './shared/catalogue';
import { useScopedQuery } from './shared/useScopedQuery';
import type { MaybeId, QueryFlags } from './shared/types';

export function partnerOrganisationsListOptions() {
  return queryOptions({
    queryKey: partnerOrganisationKeys.list(),
    queryFn: ({ signal }) => getPartnerOrganisations(signal),
    staleTime: PARTNER_ORGANISATIONS_STALE_TIME_MS,
  });
}

export function partnerAnnualGmvOptions() {
  return queryOptions({
    queryKey: partnerOrganisationKeys.annualGmv(),
    queryFn: ({ signal }) => getPartnerAnnualGmv(signal),
  });
}

export function partnerOrganisationDetailOptions(organisationId: string) {
  return queryOptions({
    queryKey: partnerOrganisationKeys.detail(organisationId),
    queryFn: ({ signal }) => getPartnerOrganisation(organisationId, signal),
    staleTime: PARTNER_ORGANISATION_DETAIL_STALE_TIME_MS,
  });
}

export function partnerOrganisationProfileOptions(organisationId: string) {
  return queryOptions({
    queryKey: partnerOrganisationKeys.profile(organisationId),
    queryFn: ({ signal }) =>
      getPartnerOrganisationProfile(organisationId, signal),
    staleTime: PARTNER_ORGANISATION_PROFILE_STALE_TIME_MS,
  });
}

export function filterByPartnerType(
  partnerOrganisations: readonly PartnerOrganisationListRow[],
  type: MakerOrganisationType
) {
  return partnerOrganisations.filter(
    (partnerOrganisation) => partnerOrganisation.type === type
  );
}

export function filterActive(
  partnerOrganisations: readonly PartnerOrganisationListRow[]
) {
  return partnerOrganisations.filter(
    (partnerOrganisation) => partnerOrganisation.isActive
  );
}

export function usePartnerOrganisationsQuery({
  enabled = true,
}: QueryFlags = {}) {
  const query = useCatalogueQuery(partnerOrganisationsListOptions, {
    enabled,
  });

  const partnerOrganisations = computed(() => query.data.value ?? []);

  return { partnerOrganisations, query };
}

export function usePartnerAnnualGmvQuery({ enabled = true }: QueryFlags = {}) {
  const query = useCatalogueQuery(partnerAnnualGmvOptions, { enabled });

  const annualGmvByPartner = computed(() => query.data.value ?? {});

  return { annualGmvByPartner, query };
}

export function usePartnerOrganisationProfileQuery(
  organisationId: MaybeId,
  flags?: QueryFlags
) {
  const query = useScopedQuery(
    organisationId,
    partnerOrganisationProfileOptions,
    { ...flags, refetchOnWindowFocus: true }
  );

  const partnerOrganisationProfile = computed(() => query.data.value);

  return { partnerOrganisationProfile, query };
}

export function usePartnerOrganisationDetailQuery(
  organisationId: MaybeId,
  flags?: QueryFlags
) {
  const query = useScopedQuery(
    organisationId,
    partnerOrganisationDetailOptions,
    {
      ...flags,
      refetchOnWindowFocus: true,
    }
  );

  const partnerOrganisation = computed(() => query.data.value);

  return { partnerOrganisation, query };
}

export function useInvalidatePartnerOrganisations() {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: partnerOrganisationKeys.list(),
    });
}

export function useInvalidatePartnerAnnualGmv() {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: partnerOrganisationKeys.annualGmv(),
    });
}

// Refetches the annual GMV only when its stale time has passed.
export function useRefetchPartnerAnnualGmvIfStale() {
  const queryClient = useQueryClient();

  return () =>
    queryClient.refetchQueries({
      queryKey: partnerOrganisationKeys.annualGmv(),
      stale: true,
    });
}

/** After a write on one partner organisation: its detail and the list it appears in. */
export function useInvalidatePartnerOrganisationDetail() {
  const queryClient = useQueryClient();

  return (organisationId: string) =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: partnerOrganisationKeys.detail(organisationId),
      }),
      queryClient.invalidateQueries({
        queryKey: partnerOrganisationKeys.list(),
      }),
    ]);
}
